import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { StyledCard, StyledCardContent, Text, ContainedButton, Progress } from '../../index';

import PatientList from '../Patient/PatientList';
import AddPatientDialog from '../Dialog/AddPatientDialog';

import { GET_PRIMARY_PATIENTS, UPDATE_PRIMARY_PATIENTS } from '../../../services/provider';
import { alertError, alertSuccess } from '../../../redux/stateActions';

const PrimaryPatient = ({ prevTab }) => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user);

    const [open, setOpen] = useState(false);

    /*--------------- Primary Patient List ---------------*/
    const [patientListData, setPatientListData] = useState([]);
    const {
        loading: getPrimaryPatientLoading,
        error: getPrimaryPatientError,
        data: getPrimaryPatientData,
        refetch: getPrimaryPatientRefetch,
    } = useQuery(GET_PRIMARY_PATIENTS, {
        variables: { id: user.staff.id },
        skip: _.isEmpty(user),
    });

    useEffect(() => {
        getPrimaryPatientRefetch();
    }, []);

    useEffect(() => {
        if (getPrimaryPatientError) {
            dispatch(alertError(`Cannot get Primary Client: ${getPrimaryPatientError.message}`));
        }

        if (getPrimaryPatientData) {
            setPatientListData(getPrimaryPatientData.staff.primaryPatients);
        }
    }, [getPrimaryPatientData, getPrimaryPatientError]);

    /*--------------- Update Primary Patients ---------------*/
    const [
        updatePrimaryPatient,
        { data: updatePrimaryPatientData, error: updatePrimaryPatientError },
    ] = useMutation(UPDATE_PRIMARY_PATIENTS);

    useEffect(() => {
        if (updatePrimaryPatientError) {
            dispatch(
                alertError(`Cannot Update Primary Clients: ${updatePrimaryPatientError.message}`)
            );
            getPrimaryPatientRefetch();
        }
        if (updatePrimaryPatientData) {
            dispatch(alertSuccess('Primary Client updated'));
            getPrimaryPatientRefetch();
        }
    }, [updatePrimaryPatientError, updatePrimaryPatientData]);

    const handleAddPatient = async (patients) => {
        setPatientListData(patients);

        const primaryArr = patients.map((patient) => patient.id);
        updatePrimaryPatient({ variables: { id: user.staff.id, primaryPatients: primaryArr } });
    };

    const handleRemovePrimaryPatient = (id) => {
        const newPatientListData = patientListData.filter((patient) => patient.id !== id);
        handleAddPatient(newPatientListData);
    };

    return (
        <StyledCard style={{ marginTop: '20px', padding: '30px 50px 24px 35px' }}>
            {getPrimaryPatientLoading && <Progress />}
            <AddPatientDialog
                open={open}
                selectedPatients={patientListData}
                onAdd={handleAddPatient}
                onCancel={() => setOpen(false)}
                title="Add Primary Patient"
            />
            <StyledCardContent
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    rowGap: '15px',
                    minHeight: '450px',
                }}
            >
                <Text $fontSize="24px" $bold>
                    Primary Client List
                </Text>

                <PatientList
                    data={patientListData}
                    onDelete={handleRemovePrimaryPatient}
                    prevTab={prevTab}
                />

                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'flex-end',
                        columnGap: '15px',
                        flexGrow: 1,
                        marginTop: '5px',
                    }}
                >
                    <ContainedButton
                        style={{ height: '32px', width: 'auto' }}
                        onClick={() => setOpen(true)}
                        $noShadow
                    >
                        + Add Client
                    </ContainedButton>
                </div>
            </StyledCardContent>
        </StyledCard>
    );
};

PrimaryPatient.propTypes = {
    data: PropTypes.array,
    prevTab: PropTypes.number,
};

export default PrimaryPatient;
